/**
 * Root ("/") route.
 *
 * Renders whichever Studio page an editor flagged "Set as Home" (Pages list →
 * is_home) — exactly as `/p/<slug>` renders it, through the same component
 * and props loader (components/page/StudioPage.tsx,
 * lib/pageBuilder/studioPageProps.ts). It used to carry its own copy, which
 * never gained the tenant root: `/` showed the skeleton navbar and footer
 * while the same page at `/p/<slug>` showed the tenant's own.
 *
 * Rendered here rather than redirected to `/p/<slug>`: Studio's home_href
 * points the header logo at "/" precisely because this route renders the
 * flagged page, and a redirect would cost every visitor a hop.
 *
 * No page flagged (or the flagged page isn't published), or Studio
 * unreachable → redirect to /home, the landing every tenant got before this
 * route existed. A tenant package can still override this file with its own
 * src/pages/index.tsx via the route-contribution overlay (§5b).
 */
import type { GetServerSideProps } from 'next'
import StudioPage from '@/components/page/StudioPage'
import { loadStudioPageProps } from '@/lib/pageBuilder/studioPageProps'
import { requestedLocale } from '@/lib/localization'
import { fetchHomePageSchema } from '@/lib/studioApi'

const FALLBACK = { redirect: { destination: '/home', permanent: false } }

export const getServerSideProps: GetServerSideProps = async ctx => {
	const locale = requestedLocale({ locale: ctx.locale, query: ctx.query })
	try {
		const props = await loadStudioPageProps(ctx, {
			locale,
			label: '(home)',
			fetchPage: () => fetchHomePageSchema(locale),
		})
		return props ? { props } : FALLBACK
	} catch {
		// Connectivity / misconfig guard — never leave "/" unresolved.
		return FALLBACK
	}
}

export default StudioPage
